import React, { useCallback, useEffect, useState } from 'react';
import lottie from 'lottie-web';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconColorBit, Button } from '@sololearnorg/sol';

import { i18n } from '../../../symphony';
import { useFadeOut } from '../SlHooks/useFadeOut';
import { SlTransition } from '../SlTransition/SlTransition';
import { animateReward } from '../../private/utils/helpers';

import celebration from './animations/celebration.json';
import boosterCelebration from './animations/booster-celebration.json';
import fallingBits from './animations/falling-bits.json';

import s from './SlCompleteCelebration.scss';

export enum AnimationId {
    Celebration,
	Booster,
    FallingBits,
}
export const animationData = {
	[AnimationId.Celebration]: celebration,
	[AnimationId.Booster]: boosterCelebration,
	[AnimationId.FallingBits]: fallingBits,
};

interface ISlCompleteCelebrationProps {
    animationId?: AnimationId;
    title: string;
    description: string;
    xp?: number;
    bits?: number;
    onContinue: () => void;
    buttonText?: string;
}

export function SlCompleteCelebration({
	animationId = AnimationId.Celebration,
	title,
	description,
	xp: xpTotal,
	bits: bitsTotal,
	onContinue,
	buttonText,
}: ISlCompleteCelebrationProps): JSX.Element {
	useStyles(s);
	const [xp, setXp] = useState<number>(0);
	const [bits, setBits] = useState<number>(0);

	const { isFadeOutStarted, startFadeOut } = useFadeOut(600);

	const animateRewards = useCallback((): void => {
		const animateXps = (callback?: () => void) => animateReward(0, xpTotal, setXp, callback);

		const animateBits = (callback?: () => void) => animateReward(0, bitsTotal, setBits, callback);

		if (xpTotal) {
			animateXps(animateBits);
		} else if (bitsTotal) {
			animateBits();
		}
	}, [xpTotal, bitsTotal]);

	useEffect(() => {
		if (xpTotal || bitsTotal) {
			setTimeout(() => {
				animateRewards();
			}, 1300);
		}
	}, [xpTotal, bitsTotal]);

	useEffect(() => {
		lottie.loadAnimation({
			animationData: animationData[animationId],
			container: document.getElementsByClassName(
				'sl-complete-celebration__animation',
			)[0],
			loop: false,
		});
	}, []);

	return (
		<div className="sl-complete-celebration__container">
			<div className="sl-complete-celebration__content">
				<SlTransition
					fadeIn={{ delay: 0.1, duration: 0.3 }}
					fadeOut={{ delay: 0, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<div className="sl-complete-celebration__animation-container">
						<div className="sl-complete-celebration__animation" />
					</div>
				</SlTransition>
				<SlTransition
					fadeIn={{ delay: 0.5, duration: 0.3 }}
					fadeOut={{ delay: 0.1, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<>
						<div className="sl-complete-celebration__title">
							{i18n.t(title)}
						</div>
						<div className="sl-complete-celebration__description">
							{i18n.t(description)}
						</div>
					</>
				</SlTransition>
				{xpTotal || bitsTotal ? (
					<SlTransition
						fadeIn={{ delay: 0.9, duration: 0.3 }}
						fadeOut={{ delay: 0.2, duration: 0.3 }}
						isFadeOutStarted={isFadeOutStarted}
						style={{ width: '100%' }}
					>
						<div className="sl-complete-celebration__reward">
							<div className="sl-complete-celebration__reward__item title">
								{i18n.t('celebration.reward-title')}
							</div>
							{xpTotal > 0 && (
								<SlTransition
									fadeIn={{ delay: 1.3, duration: 0.5 }}
								>
									<div className="sl-complete-celebration__reward__item value">
										<span className="sl-complete-celebration__reward__item__number">
											+
											{xp}
										</span>
										XP
									</div>
								</SlTransition>
							)}
							{bitsTotal > 0 && (
								<SlTransition
									fadeIn={{
										delay: xpTotal > 0 ? 2.3 : 1.3,
										duration: 0.5,
									}}
								>
									<div className="sl-complete-celebration__reward__item value">
										<span className="sl-complete-celebration__reward__item__number">
											+
											{bits}
										</span>
										<div className="sl-complete-celebration__reward__item bits-icon">
											<IconColorBit />
										</div>
									</div>
								</SlTransition>
							)}
						</div>
					</SlTransition>
				) : null}
			</div>

			<div className="sl-complete-celebration__footer">
				<SlTransition
					fadeIn={{ delay: 0.8, duration: 0.3 }}
					fadeOut={{ delay: 0.3, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<Button onClick={() => startFadeOut(onContinue)}>
						{buttonText || 'Continue'}
					</Button>
				</SlTransition>
			</div>
		</div>
	);
}
